  // 앱/웹 공통: store에서 guestUserId 읽기
  // store에 없으면 query에서 읽기 (웹 다이렉트 접근 대응)
  // query.id 로 명시적으로 새 사주를 지정한 경우 query 가 우선
  const queryId = Number(route.query.id) || null;
  const newId = queryId || mobileSajuLnbStore.guestUserId || null;
  if (newId && (queryId || !mobileSajuLnbStore.guestUserId)) {
    mobileSajuLnbStore.setGuestUserId(newId);
  }<script setup>
// =============== 1. 초기 설정 ===============
// 외부 라이브러리
import { useStore } from 'vuex';
import { storeToRefs } from 'pinia';

// API
import { fetchBaseServiceAPI } from '@/api/baseServiceApi';

// Composables
import { useGesture } from '@/composables/calendar/useGesture';
import { useAnalysisServices } from '@/composables/saju-insight/useAnalysisServices';
import { useContentRate } from '@/composables/saju-insight/useContentRate';
import { useSajuData } from '@/composables/saju-insight/useSajuData';
import { useRnBridge } from '@/composables/useRnBridge';
import { isObjectEmpty, padNumber } from '@/composables/functions';

// Store
import { useAnalysisStore } from '@/store/modules/analysis';
import { usePaymentsStore } from '@/store/modules/payments';
import { useAnalysisDataStore } from '@/store/modules/analysisDataStore';
import { useMobileSajuLnbStore } from '@/store/modules/mobileSajuLnb';

// Constants
import {
  MOBILE_PERSONALITY_CONTENTS,
  MOBILE_PERSONALITY_SECTION_PAGE_MAP,
} from '@/pages/mobile/analysis-saju-personality/constants';
import { personalityComponentMap } from '@/pages/mobile/analysis-saju-personality/summaryComponentMap';

// Components
import PersonalityCover from '@/pages/mobile/analysis-saju-personality/_summary_01.vue';
import { addContentLogAPI } from '@/api/logServiceApi';
import PersonalityTOC from '@/pages/mobile/analysis-saju-personality/_summary_02.vue';
import PopDesc from '@/pages/mobile/analysis-saju-personality/_pop_desc.vue';
import PopAnalysisInfo from '@/components/saju-insight/common/PopAnalysisInfo.vue';
import PopAnalysisInfoOther from '@/components/saju-insight/common/PopAnalysisInfoOther.vue';
import AnalysisLoadingOverlay from '@/components/saju-insight/common/AnalysisLoadingOverlay.vue';
import SurveyCard from '@/components/calendar/drawer/pages/cards/SurveyCard.vue';
import AlertComponent from '@/components/common/Alert.vue';
import IconClose01 from '@/components/svg/IconClose01.vue';
import IconList from '@/components/svg/IconList.vue';

provide('fullWidthMode', true);

definePage({ meta: { layout: 'm-saju-insight' } });

const emit = defineEmits(['close']);

const route = useRoute();
const router = useRouter();
const analysisStore = useAnalysisStore();
const analysisDataStore = useAnalysisDataStore();
const paymentsStore = usePaymentsStore();
const { serviceData, serviceContents, fetchContentByKey } = useAnalysisServices();
const mobileSajuLnbStore = useMobileSajuLnbStore();

const { sajuData, pData, pSubData, baseCode, transdata } = storeToRefs(analysisDataStore);

// =============== 2. Alert / Dialog ===============
const { alertRef, dialogTitle, dialogText, dialogNextUrl, showAlert } = useDialogManager();

// =============== 3. 웹뷰 통신 ===============
const guestUserId = ref(null);
const bottomInset = ref(0);
const isInitializing = ref(true);

const { debuggingHandler } = useRnBridge();

// =============== 4. PC 환경 ===============
const store = useStore();
const userId = ref(null);
userId.value = store.getters['user/id'];

// =============== 5. 사주 데이터 ===============
const { initWithValidation, isLoadingSajuData } = useSajuData();

const closeSajuInsight = () => {
  router.push('/saju-service');
};

const initializeSajuData = () =>
  initWithValidation(guestUserId.value, { showAlert, onRestricted: closeSajuInsight });

// =============== 6. 데이터 조회 ===============

// =============== 6-1. 이용중인 분석 정보 ===============

/** 이용중인 성향분석 정보 요청 중 상태 */
const isLoadingAnalysisInfo = ref(false);

/** 이용중인 성향분석 정보 요청 완료 여부 */
const hasLoadedAnalysisInfo = ref(false);

/** 이용중인 성향분석 정보 요청 */
const getAnalysisInfo = async () => {
  isLoadingAnalysisInfo.value = true;
  hasLoadedAnalysisInfo.value = false;

  try {
    await fetchContentByKey('personality');
    hasLoadedAnalysisInfo.value = true;
  } catch (e) {
    debuggingHandler({
      action: 'error',
      message: `성향분석 > 1. 이용중인 성향분석 정보 요청 실패: ${e}`,
    });
  } finally {
    isLoadingAnalysisInfo.value = false;
  }
};

// =============== 6-2. 분석 파라미터 ===============
/** 성향분석 데이터 요청 중 상태 */
const isFetchingPersonality = ref(false);

/** 성향분석 데이터 요청 완료 여부 */
const hasFetchedPersonality = ref(false);

/** 성향분석 데이터 요청 params */
const personalityParams = computed(() => {
  if (!sajuData.value || isObjectEmpty(sajuData.value)) return null;

  return {
    guestUserId: guestUserId.value,
    lang: 'KOR',
    birth: sajuData.value.solar ?? null,
    userName: sajuData.value.inputdata.name ?? '',
    bln: sajuData.value.transdata.lucknum ?? null,
    gender: Number(sajuData.value.inputdata.gender) ?? null,
    unknownTime: sajuData.value.inputdata.unknown_time ?? null,
    analysisTypeId: 3,
  };
});

// =============== 6-3. 기본 데이터 요청 ===============
/** 성향분석 기본 데이터 요청 */
const fetchSubData = async () => {
  hasFetchedPersonality.value = false;
  isFetchingPersonality.value = true;
  try {
    if (isObjectEmpty(analysisDataStore.pSubData)) {
      const subDataParams = {
        ...personalityParams.value,
        analysisTypeId: 8,
      };

      const result = await fetchBaseServiceAPI(subDataParams);

      if (result?.data?.성향종합분석) {
        analysisDataStore.setPSubData(result?.data?.성향종합분석);
        analysisDataStore.setPData({});

        hasFetchedPersonality.value = true;
      }
    }
  } catch (e) {
    debuggingHandler({ action: 'error', message: `성향분석 > 2-1. 기본 데이터 요청 실패: ${e}` });
  } finally {
    isFetchingPersonality.value = false;
  }
};

// =============== 6-4. 분석권 데이터 요청 ===============
/** 내 성향분석 데이터 요청 */
const fetchPersonalityData = async () => {
  hasFetchedPersonality.value = false;
  isFetchingPersonality.value = true;
  try {
    if (isObjectEmpty(analysisDataStore.pData)) {
      const result = await fetchBaseServiceAPI(personalityParams.value);

      if (result?.data) {
        analysisDataStore.setPData(result?.data);
        analysisDataStore.setPSubData({});

        hasFetchedPersonality.value = true;

        analysisStore.setAnalysisResult(result?.data);
      }
    }
  } catch (e) {
    debuggingHandler({ action: 'error', message: `성향분석 > 2-2. 분석권 데이터 요청 실패: ${e}` });
  } finally {
    isFetchingPersonality.value = false;
  }
};

// =============== 6-5. 데이터 초기화 ===============
/** 성향분석 데이터 초기화 */
const initPersonalityData = async () => {
  if (!serviceData.personality.permission.value) {
    await fetchSubData();
  } else {
    await fetchPersonalityData();
  }
};

// =============== 6-6. 페이지 데이터 새로고침 ===============
/** 성향분석 페이지 데이터 로딩 상태 */
const isRefreshingPageData = ref(false);

/** 성향분석 페이지 데이터 새로고침 */
const refreshPersonalityPageData = async () => {
  isRefreshingPageData.value = true;
  try {
    await Promise.all([
      getAnalysisInfo(),
      // 권한(canAccess) + 이용내역(만료일 등)을 access로 통합 조회 (타입별 /analysis/log 제거)
      analysisStore.fetchAnalysisAccess(guestUserId.value, [3]),
      paymentsStore.fetchTicketCount(),
      paymentsStore.getOwnPoint(),
    ]);
    await initPersonalityData();
  } catch (e) {
    debuggingHandler({ action: 'error', message: `성향분석 > 페이지 새로고침 실패: ${e}` });
  } finally {
    isRefreshingPageData.value = false;
  }
};

// =============== 7. 컨텐츠 평가 ===============
const { ratingResult, ratingStatus, getRate, postRate } = useContentRate(
  'PERSONALITY_ANALYSIS',
  guestUserId,
);

// =============== 8. 페이지 상태 ===============
const currentPage = ref(0); // 현재 페이지

/** 진로 추천 데이터 개수 확인 (3개 이상이면 true, 미만이면 false) */
const showFullLayout = computed(() => {
  if (!pData.value?.진로학과?.일반추천분야) return true;
  const majorRecoListAll = Object.entries(pData.value.진로학과.일반추천분야);
  return majorRecoListAll.length >= 3;
});

/** 동적으로 전체 페이지 수 계산 */
const totalPages = computed(() => {
  // 진로 추천 데이터가 3개 이상이면 44페이지, 미만이면 43페이지
  return showFullLayout.value ? 44 : 43;
});

const showSummary02 = ref(false);

const showPopAnalysisInfo = ref(false);
const showPopAnalysisInfoOther = ref(false);

const goToCoverPage = () => {
  // 모바일은 별도 다시보기 버튼이 없어, 마지막 페이지에서 표지 복귀 시점을 다시보기로 집계
  addContentLogAPI('ap_personality_btn_restart');
  currentPage.value = 0;
  showSummary02.value = false;
};

// =============== 9. 팝업 / 모달 ===============
const showPopAnalysisInfoDo = async () => {
  if (isRefreshingPageData.value) return;

  // 성향분석권 사용중일 때,
  if (serviceData.personality.permission.value) {
    showPopAnalysisInfo.value = false;
    showPopAnalysisInfoOther.value = false;
    return;
  }

  if (
    paymentsStore?.points < serviceContents.personality.value?.pointPrice &&
    serviceData.personality.ticketCount.value === 0
  ) {
    // 포인트가 적고, 분석권도 없을 때
    // 포인트 충전 팝업 표시 (분석권 구매 팝업 X)
    showPopAnalysisInfoOther.value = false;
    showPopAnalysisInfo.value = true;
  } else {
    // 포인트 또는 분석권이 충분할 때,
    // 분석권 구매 팝업 표시 (포인트 충전 팝업 X)
    showPopAnalysisInfoOther.value = true;
    showPopAnalysisInfo.value = false;
  }
};

// =============== 하단 탭 영역 (직업추천 & 용어해석) ===============
const showPopDesc = ref(false);
const openPopDesc = () => {
  if (isRefreshingPageData.value) return;
  if (!serviceData.personality.permission.value) {
    showPopAnalysisInfoDo();
    return;
  }
  showPopDesc.value = true;
};
const closePopDesc = () => {
  showPopDesc.value = false;
};

const handleClosePop = () => {
  showPopAnalysisInfo.value = false;
};

const handleChargePop = (shortageAmount) => {
  router.push({ path: '/purchase/points', query: { next: route.fullPath, shortageAmount } });
  showPopAnalysisInfo.value = false;
};

const handleClosePopOther = () => {
  showPopAnalysisInfoOther.value = false;
};

const handleChargePopOtherProduct = () => {
  router.push('/purchase/tickets');
  showPopAnalysisInfoOther.value = false;
};

/** 성향분석 결제 성공 후 실행 */
const paymentSuccessDo = async (success) => {
  if (success) {
    try {
      await refreshPersonalityPageData();
      await getRate();
      mobileSajuLnbStore.setNeedRefresh(true);
    } catch (e) {
      debuggingHandler({ action: 'error', message: `성향분석권 결제 후 새로고침 중 오류: ${e}` });
    }
  }
};

// =============== 10. 페이지 네비게이션 ===============
/** 페이지 이전/다음 이동 로그 — 버튼/스와이프 공통, 이동이 실제 발생한 경우만 발화 */
const logPageMove = () => {
  addContentLogAPI({
    contentType: 'ap_personality_page_opt3',
    option3: String(currentPage.value),
  });
};

const clickPrevPage = () => {
  if (currentPage.value < 1) return;
  else currentPage.value -= 1;
  logPageMove();
  window.scrollTo(0, 0);
};

const clickNextPage = () => {
  if (currentPage.value === 6 && !serviceData.personality.permission.value) {
    showPopAnalysisInfoDo();
    return;
  }
  if (currentPage.value >= totalPages.value) {
    goToCoverPage();
  } else {
    currentPage.value += 1;
    logPageMove();
  }
  window.scrollTo(0, 0);
};

// 키보드 방향키로 페이지 이동 (개발용)
const onKeydown = (e) => {
  if (e.key === 'ArrowLeft') clickPrevPage();
  else if (e.key === 'ArrowRight') clickNextPage();
};
onMounted(() => window.addEventListener('keydown', onKeydown));
onUnmounted(() => window.removeEventListener('keydown', onKeydown));

/** 통합 제스처 핸들러 */
const { handleStart, handleMove, handleEnd } = useGesture({
  onSwipeLeft: clickNextPage, // 왼쪽 스와이프 → 다음 페이지 이동
  onSwipeRight: clickPrevPage, // 오른쪽 스와이프 → 이전 페이지 이동
  SWIPE_THRESHOLD: 150,
});

const handleNavigateToPage = (tocIdx) => {
  const mappedPage = MOBILE_PERSONALITY_SECTION_PAGE_MAP[tocIdx];

  if (mappedPage !== undefined) {
    // 목차 클릭 로그(무료/유료). PC와 동일 content_type 재사용
    addContentLogAPI({
      contentType: serviceData.personality.permission.value
        ? 'ap_personality_toc_paid_opt3'
        : 'ap_personality_toc_free_opt3',
      option3: String(tocIdx),
    });
    currentPage.value = mappedPage;
  } else {
    debuggingHandler({
      action: 'error',
      message: `성향분석 > 매핑되지 않은 목차 인덱스: ${tocIdx}`,
    });
  }
};

// =============== 11. 목차 UI ===============
const openFlag = ref('');
const openContentsList = () => {
  if (openFlag.value === '') openFlag.value = 'open';
  else openFlag.value = '';
};
const closeContentsList = () => (openFlag.value = '');

// 페이지별 컴포넌트 반환 함수
const getComponentByPage = (page) => {
  return personalityComponentMap[page] || null;
};

// 페이지별 CSS 클래스 반환 함수
const getComponentClass = (page) => {
  return page % 2 === 1 ? 'mobile-content-first' : 'mobile-content-second';
};

/** index값으로 페이지 이동 */
const updateIndexNo = (id, tocNo = null) => {
  // tocNo는 플로팅 목차 리스트 클릭 시에만 전달 — 프로그래밍 방식 이동은 발화하지 않음
  if (tocNo != null) {
    addContentLogAPI({
      contentType: serviceData.personality.permission.value
        ? 'ap_personality_toc_paid_opt3'
        : 'ap_personality_toc_free_opt3',
      option3: String(tocNo),
    });
  }
  if (id < 6) {
    // props.updateSelectedDescNav('')
  } else {
    if (id > 6 && !serviceData.personality.permission.value) {
      // emit('goPopAnalysis')
      showPopAnalysisInfoDo();
      return;
    }
    // props.updateSelectedDescNav('personalityJob')
  }
  // 동적 페이지 수 범위 체크
  if (id > totalPages.value) {
    console.warn(`페이지 번호 ${id}가 전체 페이지 수 ${totalPages.value}를 초과합니다.`);
    return;
  }
  currentPage.value = id;
  closeContentsList();
};

// =============== 12. 공통 액션 ===============
/** 팝업 닫기 */
const close = () => {
  router.push(`/m/saju-insight/${guestUserId.value}`);
};

const resetPopup = () => {
  currentPage.value = 0;
  showSummary02.value = false;
  showPopDesc.value = false;
};

const onClickRecharge = (shortageAmount) => {
  router.push({
    path: '/purchase/points',
    query: { next: route.fullPath, shortageAmount },
  });
};

const handelShowSummary02Open = () => {
  // 조회 로그(무료/유료) — 안내서 보기/시작 시점. PC와 동일 content_type 재사용(디바이스 구분은 백엔드 option2).
  addContentLogAPI(
    serviceData.personality.permission.value
      ? 'ap_personality_btn_guide_paid'
      : 'ap_personality_btn_guide_free',
  );
  showSummary02.value = true;
  window.scrollTo(0, 0);
};

// =============== 13. Watch ===============
watch(
  () => mobileSajuLnbStore.guestUserId,
  (newId) => {
    if (newId) guestUserId.value = newId;
  },
);

watch(guestUserId, async (newId, oldId) => {
  if (newId && newId !== oldId) {
    const result = await initializeSajuData();
    if (result?.failed || result?.unknownTime || result?.futureSaju) return;
    // 사주 ID 변경 시 이전 사주의 분석 데이터 초기화 (base-service 재호출 보장)
    analysisDataStore.setPData({});
    analysisDataStore.setPSubData({});
    await refreshPersonalityPageData();
    await getRate();
  }
});

// 인덱스
// const indexNo = ref(1)
watch(currentPage, (newPage) => {
  analysisStore.setPersonalityIndex(newPage);
  // currentPage.value = newIndex
});

watch(
  () => analysisStore.selectedContentIndex,
  (newIdx) => {
    const target = MOBILE_PERSONALITY_CONTENTS[newIdx];
    if (target?.id) updateIndexNo(target.id);
  },
  { immediate: true },
);

// =============== 14. 라이프사이클 ===============
onMounted(async () => {
  // 웹 환경: 비로그인 접근 차단
  if (!userId.value && !sessionStorage.getItem('X-Token')) {
    showAlert({ title: '로그인 후 이용 가능합니다.', nextUrl: () => router.push('/auth/login') });
    return;
  }

  // 페이지 진입 로그 — 모바일은 LNB가 없어 진입 시점에 발화 (신규 type, DB 등록 후 작동)
  addContentLogAPI('ap_personality_page_view');

  // 앱/웹 공통: store에서 guestUserId 읽기
  // store에 없으면 query에서 읽기 (웹 다이렉트 접근 대응)
  // query.id 로 명시적으로 새 사주를 지정한 경우 query 가 우선
  const queryId = Number(route.query.id) || null;
  const newId = queryId || mobileSajuLnbStore.guestUserId || null;
  if (newId && (queryId || !mobileSajuLnbStore.guestUserId)) {
    mobileSajuLnbStore.setGuestUserId(newId);
  }

  // 초기화 완료 설정 (웹: 여기서 처리 / 앱 직접 접근: onInit에서 처리 / 웹뷰 내 라우팅: 여기서 처리)
  isInitializing.value = false;

  // 같은 ID여도 강제 재조회 (브라우저 캐시 대응)
  if (newId) {
    guestUserId.value = null;
    await nextTick();
    guestUserId.value = newId;
  }
});

onUnmounted(() => {
  analysisStore.setPersonalityIndex(1);
  analysisStore.setSelectedContentIndex(null);
});
</script>

<template>
  <!-- 분석권 및 포인트 부족 -->
  <PopAnalysisInfo
    :isVisible="showPopAnalysisInfo"
    :analysisType="serviceContents.personality.value"
    @close="handleClosePop"
    @charge="handleChargePop"
  />
  <!-- 분석권 또는 포인트 사용 가능 -->
  <PopAnalysisInfoOther
    :isVisible="showPopAnalysisInfoOther"
    :analysisType="serviceContents.personality.value"
    :ticketCountData="serviceData.personality.ticketData.value"
    :ticketCount="serviceData.personality.ticketCount.value"
    @close="handleClosePopOther"
    @charge="handleChargePopOtherProduct"
    @payment-success="paymentSuccessDo"
  />

  <div
    class="pop-saju-reading-container mobile st-analysis-personality"
    @mousedown="handleStart"
    @mousemove="handleMove"
    @mouseup="handleEnd"
    @touchstart="handleStart"
    @touchmove="handleMove"
    @touchend="handleEnd"
  >
    <AnalysisMobileHeader title="낭만 성향분석" :guestUserId="guestUserId" @close="close" />
    <div v-if="currentPage === 0" class="pop-ana-saju-reading-inner intro">
      <PersonalityCover
        v-if="currentPage === 0 && !showSummary02"
        :guestUserId="guestUserId"
        :guestUserName="paramData?.userName || mobileSajuLnbStore?.userName"
        :ticketCount="paymentsStore?.analysisTicketCount || 0"
        :getAnalysisLogDataList="serviceData.personality.logData.value"
        :hasPermission="serviceData.personality.permission.value"
        @handleNavigateToPage="handleNavigateToPage"
        @handelShowSummary02Open="handelShowSummary02Open"
        @recharge="onClickRecharge"
      />
      <AnalysisLoadingOverlay
        :loading="
          currentPage === 0 &&
          showSummary02 &&
          (isInitializing || isLoadingSajuData || isRefreshingPageData)
        "
        :message="'성향 분석중입니다!'"
      />
      <PersonalityTOC
        v-if="currentPage === 0 && showSummary02 && !isRefreshingPageData"
        :guestUserName="paramData?.userName || mobileSajuLnbStore?.userName"
        :ticketCount="paymentsStore?.analysisTicketCount || 0"
        :hasPermission="serviceData.personality.permission.value"
        :initializing="isInitializing || isLoadingSajuData || isRefreshingPageData"
        @navigateToPage="handleNavigateToPage"
        @updateShowSummary02="(value) => (showSummary02 = value)"
        @goPopAnalysis="showPopAnalysisInfoDo"
        @recharge="onClickRecharge"
      />
    </div>

    <div v-if="currentPage !== 0" class="pop-ana-saju-reading-inner st-page st-content-page">
      <div v-if="currentPage >= 1 && currentPage <= 4" class="pop-body mobile">
        <component :is="getComponentByPage(currentPage)" :class="getComponentClass(currentPage)" />
      </div>
      <div v-if="currentPage >= 5 && currentPage < totalPages" class="pop-body mobile">
        <component
          :is="getComponentByPage(currentPage)"
          :class="getComponentClass(currentPage)"
          :userName="analysisStore.userName"
          :pData="pData"
          :pSubData="pSubData"
          :showFullLayout="showFullLayout"
          :isLastPageBefore="currentPage === totalPages - 1"
        />
      </div>

      <div v-if="currentPage === totalPages" class="pop-body mobile rating">
        <div class="sr__no-content-page">
          <SurveyCard
            style="width: 100%"
            :ratingResult="ratingResult"
            :ratingStatus="ratingStatus"
            @postRate="postRate"
          />
        </div>
      </div>

      <div class="contentsListBtnBox mobile">
        <div class="list-box" :class="openFlag">
          <ul class="list-li-box">
            <li
              class="li mobile"
              :class="{ 'li--locked': !serviceData.personality.permission.value }"
              v-for="(item, tocIdx) in MOBILE_PERSONALITY_CONTENTS.filter(
                (content) => content.title !== '',
              )"
              :key="item.id"
              @click="serviceData.personality.permission.value && updateIndexNo(item.id, tocIdx + 1)"
            >
              {{ item.title }}
            </li>
          </ul>
        </div>
      </div>
      <div class="pop-footer-page-box">
        <div class="pop-desc-box">
          <button class="btn-desc" @click="openPopDesc">📖 직업추천 & 용어해석</button>
        </div>
        <div class="pop-pagination" v-if="currentPage !== 0">
          <IconList
            @click="openContentsList('open')"
            v-if="openFlag === ''"
            style="margin-top: 3px"
          />
          <IconClose01
            @click="openContentsList('')"
            v-if="openFlag === 'open'"
            style="margin-top: 3px"
          />
          <button class="pop-pagination__prev" @click="clickPrevPage">
            <i class="icon-chevron"></i>
          </button>
          <div class="pop-pagination__index" @click="openContentsList">
            {{ padNumber(currentPage) }} / {{ totalPages }}
          </div>
          <button class="pop-pagination__next" @click="clickNextPage">
            <i class="icon-chevron right"></i>
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- 직업추천 & 용어해석 팝업 -->
  <PopDesc
    v-model="showPopDesc"
    :sajuData="sajuData"
    :transdata="transdata"
    :baseCode="baseCode"
    :guestUserId="guestUserId"
    @close="closePopDesc"
  />

  <AlertComponent ref="alertRef" :title="dialogTitle" :text="dialogText" :nextUrl="dialogNextUrl" />
</template>

<style lang="scss">
@import '@/assets/nangman_style/popup/saju-reading_mobile.scss';
@import '@/assets/nangman_style/mobile/saju-reading-common.scss';
@import '@/assets/nangman_style/mobile/saju-reading-common-page.scss';
@import '@/assets/nangman_style/_analysis_main_color.scss';

/* 네이버 인앱브라우저 주소창 오버레이 방지: 문서 레벨 스크롤 전환 */
.pop-saju-reading-container.mobile.st-analysis-personality {
  height: auto;
  min-height: 100vh;
  overflow: visible;

  > .header {
    position: sticky;
    top: 0;
    z-index: 20;
  }
}

.pop-ana-saju-reading-inner {
  height: auto;
  min-height: calc(100vh - 52px - var(--safe-area-top, 0px));
  overflow: visible;
  &.intro {
    overflow: visible;
    padding-bottom: var(--safe-area-bottom, 0px);
  }
  &.st-page {
    height: auto;
    min-height: calc(100vh - 52px - var(--safe-area-top, 0px));
    overflow: visible;
    padding-bottom: calc(52px + var(--safe-area-bottom, 0px));
  }
  .btn-footer-box {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 16px;
    .btn-white {
      display: flex;
      width: 100%;
      height: 48px;
      padding: 14px 20px;
      justify-content: center;
      align-items: center;
      gap: 4px;
      border-radius: 6px;
      border: 1px solid var(--Gray-300, #ebebeb);
      background: var(--gray-100-white, #fff);
      box-shadow: 0px -10px 10px 0px rgba(0, 0, 0, 0.05);
      // color: var(--Gray-800, #333);
      color: $analysis-personality;
      text-align: center;
      font-family: Pretendard;
      font-size: 15px;
      font-style: normal;
      font-weight: 700;
      line-height: 20px;
    }
  }
  .pop-body {
    height: auto;
    padding: 0px;
    flex: 1;
    overflow: visible;
    .v-row {
      margin: 0px !important;
    }
    .personality-wrapper {
      padding: 24px 16px;
    }
    &.rating {
      height: 100%;
    }
  }
}
.st-analysis-personality {
  .pop-ana-saju-reading-inner,
  .overview-cover {
    background: $analysis-personality;
  }
  .pop-ana-saju-reading-inner {
    // padding-bottom: 120px;
  }
}
.st-analysis-personality .pop-ana-saju-reading-inner .pop-body {
  background: #fff;
}

.st-analysis-personality .pop-ana-saju-reading-inner.st-content-page {
  background-color: #fff !important;
  padding-bottom: calc(52px + var(--safe-area-bottom, 0px));
  overflow-x: hidden !important;

  .contentsListBtnBox.mobile {
    position: fixed;
    bottom: calc(52px + var(--safe-area-bottom, 0px));
    right: 0;
  }
}
</style>
